import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Collapse
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ScheduleSection = ({
    missionData,
    setMissionData,
    handleFieldChange,
    originalMissionData,
    setIsDirty,
}) => {
    const sectionStyles = {
        backgroundColor: '#ffffff',
        border: '1px solid #dcdcdc',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        //boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    };
    const [isExpanded, setIsExpanded] = useState(true);

    const handleResetSchedule = () => {
        setIsDirty(true);

        updateMissionField('scheduledAt', originalMissionData?.scheduledAt ?? null);
        updateMissionField('dateFlexibility', originalMissionData?.dateFlexibility ?? 0);
        updateMissionField('timeFlexibility', originalMissionData?.timeFlexibility ?? 0);

        //setMissionData((prevData) => ({
        //    ...prevData,
        //    scheduledAt: originalMissionData?.scheduledAt ?? null,
        //    dateFlexibility: originalMissionData?.dateFlexibility ?? 0,
        //    timeFlexibility: originalMissionData?.timeFlexibility ?? 0,
        //}));
    };

    const updateMissionField = (fieldName, fieldValue) => {
        setIsDirty(true);

        // Update missionData
        setMissionData((prevData) => ({
            ...prevData,
            [fieldName]: fieldValue,
        }));

        // Call the parent's handleFieldChange
        handleFieldChange({ target: { name: fieldName, value: fieldValue } });
    };

    useEffect(() => {
        setIsExpanded(!missionData?.completedAt);
    }, [missionData]);

    return (
        <Box sx={sectionStyles}>
            {/* Section Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: isExpanded ? '0px' : '0',
                    cursor: 'pointer',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <CalendarTodayIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Schedule
                </Typography>
                <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={isExpanded}>
                {/* Reset Schedule */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        marginLeft: 0,
                        paddingLeft: 0,
                        paddingTop: '8px',
                        width: 'fit-content',
                    }}
                >
                    <Button
                        variant="text"
                        color="error"
                        onClick={handleResetSchedule}
                        sx={{
                            textTransform: 'none',
                            fontWeight: '400',
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: 'transparent', // Ensures no background color on hover
                                color: '#1565c0', // Slightly darker blue on hover
                            },
                            whiteSpace: 'nowrap',
                            padding: 0,
                            margin: 0,
                        }}
                    >
                        Reset Schedule
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '32px', marginBottom: '32px' }}>
                    {/* Scheduled Date */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '200px', gap: '20px' }}>
                        <DatePicker
                            label="Scheduled Date"
                            value={missionData?.scheduledAt ? dayjs(missionData.scheduledAt) : null}
                            onChange={(newDate) => {
                                updateMissionField('scheduledAt', newDate ? newDate.toISOString() : null);
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                },
                            }}
                        //renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                        {/* Date Flexibility */}
                        <TextField
                            select
                            id="date-flexibility"
                            name="dateFlexibility"
                            label="Date Flexibility"
                            value={missionData?.dateFlexibility ?? 0}
                            onChange={(e) => {
                                const selectedValue = parseInt(e.target.value, 10);
                                updateMissionField('dateFlexibility', selectedValue === 0 ? null : selectedValue);
                            }}
                            fullWidth
                        >
                            <MenuItem value={0}>None</MenuItem>
                            <MenuItem value={1}>+/- 1 day</MenuItem>
                            <MenuItem value={2}>+/- 2 days</MenuItem>
                            <MenuItem value={3}>+/- 3 days</MenuItem>
                        </TextField>
                    </Box>

                    {/* Scheduled Time */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '200px', gap: '20px' }}>
                        <TimePicker
                            label="Scheduled Time"
                            value={missionData?.scheduledAt ? dayjs(missionData.scheduledAt) : null}
                            onChange={(newTime) => {
                                if (newTime) {
                                    const currentDate = missionData?.scheduledAt
                                        ? dayjs(missionData.scheduledAt)
                                        : dayjs();
                                    const updatedDate = currentDate
                                        .hour(newTime.hour())
                                        .minute(newTime.minute())
                                        .second(0);
                                    updateMissionField('scheduledAt', updatedDate.toISOString());
                                }
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                },
                            }}
                        //renderInput={(params) => <TextField {...params} fullWidth />}
                        />

                        {/* Time Flexibility */}
                        <TextField
                            select
                            id="time-flexibility"
                            name="timeFlexibility"
                            label="Time Flexibility"
                            value={missionData?.timeFlexibility ?? 0}
                            onChange={(e) => {
                                const selectedValue = parseInt(e.target.value, 10);
                                updateMissionField('timeFlexibility', selectedValue === 0 ? null : selectedValue);
                            }}
                            fullWidth
                        >
                            <MenuItem value={0}>None</MenuItem>
                            <MenuItem value={1}>+/- 1 hour</MenuItem>
                            <MenuItem value={2}>+/- 2 hours</MenuItem>
                            <MenuItem value={3}>+/- 3 hours</MenuItem>
                            <MenuItem value={4}>+/- 4 hours</MenuItem>
                            <MenuItem value={5}>+/- 5 hours</MenuItem>
                            <MenuItem value={6}>+/- 6 hours</MenuItem>
                            <MenuItem value={7}>+/- 7 hours</MenuItem>
                            <MenuItem value={8}>+/- 8 hours</MenuItem>
                        </TextField>
                    </Box>

                    {/* Mission Duration */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '130px' }}>
                        <TextField
                            id="mission-length-est"
                            label="Duration (mins)"
                            name="missionLengthEst"
                            type="number"
                            value={missionData?.missionLengthEst || ''}
                            onChange={(e) => {
                                updateMissionField('missionLengthEst', e.target.value);
                            }}
                            fullWidth
                        />
                    </Box>
                </Box>
            </Collapse>

        </Box>
    );
};

export default ScheduleSection;